<template>
  <content-modal
    ref="embedAppModal"
    class="embed-app-modal embed-modal"
    :visible="visible"
    :close-aria-label="closeAriaLabel"
    :size="size"
    :auto-hide-off="autoHideOff"
    @modal-hide-request="actionHideRequest"
  >
    <template #title>{{ t("tinymce_plugin_embed_app_modal_title") }}</template>
    <template #content>
      <modal-content>
        <template #content>
          <overlay-loader v-if="isLoading" />
          <section v-else>
            <div class="info">
              {{ t("embed_website_app_dropdown_label") }}
            </div>
            <cv-form class="embed-app">
              <dl class="field-set type-button">
                <dd class="field-input">
                  <cv-button
                    class="btn-browse"
                    type="button"
                    kind="tertiary"
                    size="small"
                    @click="handleBrowse"
                  >
                    <svg-icon name="Embed24" kind="action" />
                    {{ t("tinymce_plugin_embed_app_browse_app_label") }}
                  </cv-button>
                </dd>
              </dl>

              <dl v-if="showAppInfo" class="field-set info-wrapper">
                <dd class="field-value">
                  <div class="image-placeholder">
                    <EmptyError v-if="appIconEmpty" class="image" />
                    <img v-else class="image" :src="selectedApp?.iconFileName" alt="" />
                  </div>
                  <div class="info">
                    <h5 class="website-title">
                      <span>{{ selectedApp?.name }}</span>
                    </h5>
                    <div class="website-description">
                      <rich-text :content="selectedApp?.description" :expanded="false" />
                    </div>
                  </div>
                </dd>
              </dl>

              <dl v-if="isLTIApp" class="field-set app-preview">
                <dd class="field-value">
                  <cv-button kind="tertiary" size="small" @click="handleBrowseApp">
                    <svg-icon name="Search24" kind="action" />
                    {{ t("embed_website_app_browse_button") }}
                  </cv-button>
                  <media-component
                    v-if="isLTIApp && showDeepLink"
                    ref="media"
                    :embed-config="embedData"
                    :context="context"
                    :caption="false"
                  />
                </dd>
              </dl>

              <dl class="field-set type-text resource-url">
                <dt class="field-label">
                  {{ t("embed_website_app_resource_url_optional_label") }}
                </dt>
                <dd class="field-input">
                  <text-input
                    v-model:value="appUrl"
                    :placeholder="urlPlaceholder"
                    :error-message="error"
                    :character-limit="250"
                    :instant="instant"
                    @change="handleUrlInput"
                    @input="handleUrlInput"
                  />
                </dd>
              </dl>
            </cv-form>
          </section>
        </template>
        <template #tools>
          <cv-button class="cancel" kind="secondary" size="field" @click="actionHideRequest">
            {{ t("button_cancel") }}
          </cv-button>
          <fetch-data-button
            kind="primary"
            size="field"
            :loading="processingState.isLoading"
            :error="processingState.error"
            :error-message="processingState.errorMessage"
            :tip-position="processingState.tipPosition"
            @click="handleSave"
          >
            <span v-if="processingState.error">{{ t("button_state_error") }}</span>
            <span v-else>{{ primaryButtonLabel }}</span>
          </fetch-data-button>
        </template>
      </modal-content>
    </template>
  </content-modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, getCurrentInstance } from "vue";
import modalEventBus from "@plugins/modal/service/event-bus";
import embedUtils from "../../utils/embed.utils";
import appListApi from "@apis/app.list.api";
import { isEmpty, isDefined } from "@utils/var.check";
import embedComponentEventBus from "@plugins/richtext/plugins/embed-website/service/event-bus";
import mediaEventBus from "@plugins/media/service/event-bus";
import EventBusUtils from "@plugins/common/service/event.bus.utils";
import ContentModal from "@plugins/modal/components/ContentModal.vue";
import ModalContent from "@plugins/modal/components/ModalContent.vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import RichText from "@plugins/richtext/components/RichText.vue";
import TextInput from "@plugins/common/components/TextInput.vue";
import FetchDataButton from "@plugins/common/components/FetchDataButton.vue";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import { useI18n } from "vue-i18n";
import { AppMetadataModel } from "@plugins/richtext/types.ts";
import { APP_TYPE } from "@plugins/richtext/richtext.constant.ts";
import EmptyError from "@images/placeholder/empty-states/EmptyImage.svg?component";
import randomUtils from "@utils/random.utils";
import { useDirty } from "@plugins/common/composables/page-dirty.ts";
import MediaComponent from "@plugins/media/components/MediaComponent.vue";

const props = defineProps<{ params: Record<string, unknown> }>();
const emit = defineEmits<{
  (e: "onHandleBrowseClick"): void;
  (e: "deeplinkChange", payload: Record<string, unknown>): void;
}>();

const { t } = useI18n();
const { passiveApiFailHandler } = useErrorHandler();
const instance = getCurrentInstance();
const dirtyId = ref(randomUtils.randomId());
const { setDirty, removeDirty, isDirty, promptComponentDirty } = useDirty(dirtyId);

interface EmbedPayload {
  type: "embed-app";
  data: AppMetadataModel | null;
}

interface DeepLinkMessage {
  url: string;
  custom?: Record<string, string>;
}

const isLoading = ref(true);
const closeAriaLabel = "Close";
const size = "large";
const autoHideOff = true;
const visible = ref(true);
const context = props.params.context as { resourceUuid: string };
const selectedApp = ref<AppMetadataModel | null>(null);
const error = ref("");
const primaryButtonLabel = t("button_save");
const processingState = ref({
  isLoading: false,
  error: false,
  errorMessage: "",
  tipPosition: "top",
});
const showDeepLink = ref(false);
const appUrl = ref("");
const ltiPreviewable = props.params.ltiPreviewable as boolean | undefined;
const isEdit = ref(false);
const embedData = ref<Record<string, unknown>>(
  (props.params.embedConfig as Record<string, unknown>) ?? {},
);

const urlPlaceholder = computed(() => "Example: https://www.website.com");
const isLTIApp = computed(() => !!(selectedApp.value && selectedApp.value.deepLinkLaunchUrl));
const instant = computed(() => !ltiPreviewable);
const showAppInfo = computed(() => !isEmpty(selectedApp.value));
const appIconEmpty = computed(() => selectedApp.value && isEmpty(selectedApp.value.iconFileName));

onMounted(() => {
  let uuid: string;

  if (props.params.embedConfig) {
    // from settings
    isEdit.value = true;
    const embedDataVal = props.params.embedConfig as {
      frameAttributes: { appuuid: string; appurl: string };
    };
    uuid = embedDataVal.frameAttributes.appuuid;

    appListApi.fetchAppMetadata(uuid).then((response) => {
      if (response.code !== "ok") {
        passiveApiFailHandler();
        return;
      }
      selectedApp.value = response.data;
      appUrl.value = embedDataVal.frameAttributes.appurl;
      showDeepLink.value = true;
      isLoading.value = false;
    }, passiveApiFailHandler);
  } else if (props.params.presetData) {
    // selected app from browse
    const presetData = props.params.presetData as { uuid: string; appUrl?: string };
    uuid = presetData.uuid;
    setDirty(dirtyId.value);
    appListApi.fetchAppMetadata(uuid).then((response) => {
      if (response.code !== "ok") {
        passiveApiFailHandler();
        return;
      }
      selectedApp.value = response.data;
      if (presetData.appUrl) {
        selectedApp.value.appUrl = presetData.appUrl;
        appUrl.value = presetData.appUrl;
      }
      if (selectedApp.value?.appType === APP_TYPE.LTI13) {
        handleBrowseApp();
      }
      isLoading.value = false;
    }, passiveApiFailHandler);
  }
});

function isProcessing() {
  return processingState.value.isLoading;
}

function setProcessing() {
  processingState.value.isLoading = true;
}

function setProcessingError() {
  processingState.value.isLoading = false;
  processingState.value.error = true;
  // short delay before removing the error state.
  setTimeout(() => {
    resetProcessingState();
  }, 3000);
}

function resetProcessingState() {
  processingState.value.isLoading = false;
  processingState.value.error = false;
}

function resetError() {
  error.value = "";
}

function handleSave() {
  if (!isDirty(dirtyId.value)) {
    visible.value = false;
    EventBusUtils.unWatchInstance(props.params.eventBusWatchInstances);
    document.body.classList.remove("bx--body--with-modal-open");
    modalEventBus.$emit("modal-hidden", instance?.vnode.key);
    return;
  }

  if (isProcessing()) {
    return;
  }
  setProcessing();
  resetError();

  if (validateAppUrl(true)) {
    const embedPayload: EmbedPayload = { type: "embed-app", data: selectedApp.value };
    if (isLTIApp.value && isDefined((embedData.value as { deeplinkUrl?: string }).deeplinkUrl)) {
      (embedPayload.data as AppMetadataModel).deeplinkUrl = (
        embedData.value as { deeplinkUrl: string }
      ).deeplinkUrl;
    }
    if (isEdit.value) {
      mediaEventBus.$emit("settingsUpdated", getSettingsData());
    } else {
      embedComponentEventBus.$emit("embedItemAdded", embedPayload);
    }
    removeDirty(dirtyId.value);
    visible.value = false;
    EventBusUtils.unWatchInstance(props.params.eventBusWatchInstances);
    document.body.classList.remove("bx--body--with-modal-open");
    modalEventBus.$emit("modal-hidden", instance?.vnode.key);
  } else {
    setProcessingError();
  }
}

function getSettingsData() {
  const slsFrame = embedUtils.getEmbedAppFrame(selectedApp.value);
  const frameAttributes = embedUtils.getValidFrameAttributes(
    (slsFrame as { attributes: Record<string, string> }).attributes,
  );

  return {
    slsFrame,
    frameAttributes,
    updateCallback: props.params.editorSaveCallback,
  };
}

async function actionHideRequest(event: Record<string, string>) {
  if (event["cv:reason"] !== "external-click") {
    const ok = await promptComponentDirty();
    if (!ok) return;
    visible.value = false;
    EventBusUtils.unWatchInstance(props.params.eventBusWatchInstances);
    document.body.classList.remove("bx--body--with-modal-open");
    modalEventBus.$emit("modal-hidden", instance?.vnode.key);
  }
}

function handleUrlInput(value: string) {
  error.value = "";
  appUrl.value = value;
  setDirty(dirtyId.value);
  if (validateAppUrl(false)) {
    if (selectedApp.value) selectedApp.value.appUrl = appUrl.value;
  } else {
    appUrl.value = value ?? "";
  }
}

function validateAppUrl(showError: boolean) {
  if (appUrl.value !== "") {
    const errorCode = embedUtils.validateAppUrl(selectedApp.value, appUrl.value) as string;
    if (errorCode === "") {
      if (showError) error.value = "";
      return true;
    } else {
      if (showError) error.value = t(errorCode);
      return false;
    }
  }
  error.value = "";
  return true;
}

function handleBrowse() {
  actionHidden();
  emit("onHandleBrowseClick");
}

function handleBrowseApp(event?: Event) {
  if (event) event.preventDefault();
  if (!selectedApp.value) return;

  const deeplinkUrl = `/lti/launch/deep-linking?toolId=${selectedApp.value.uuid}&contextId=${context.resourceUuid}`;
  embedData.value = Object.assign({}, embedData.value, { deeplinkUrl });

  window.addEventListener("message", (evt: MessageEvent) => {
    if (window.location.origin === evt.origin) {
      const raw = typeof evt.data === "string" ? JSON.parse(evt.data) : evt.data;
      const data = raw as DeepLinkMessage;
      if (data.custom && selectedApp.value) {
        selectedApp.value.custom = data.custom;
      }
      handleUrlInput(data.url);
      showDeepLink.value = false;
    }
  });
  if (ltiPreviewable) {
    // show lti iframe
    showDeepLink.value = true;
  }
  emit("deeplinkChange", { deeplinkUrl });
}

function actionHidden() {
  visible.value = false;
  EventBusUtils.unWatchInstance(props.params.eventBusWatchInstances);
  modalEventBus.$emit("modal-hidden", instance?.vnode.key);
}
</script>
